import type { TowerConfig } from "../entities/types.ts";
import { COLORS } from "../core/constants.ts";
import { RARITY } from "../core/palette.ts";
import type { Element } from "../entities/elements.ts";
import type { Tower } from "../entities/Tower.ts";
import {
  CELL_SIZE,
  GRID_WIDTH,
  TOWER_CAPABILITY,
  GOLD_TIER,
} from "../core/config.ts";
import { virusInfectionProfile } from "../systems/VirusLevels.ts";
import { ELEMENTS, ELEMENT_REGISTRY } from "../rendering/ElementRegistry.ts";
import { FIREWALL_DEFAULTS } from "../entities/Firewall.ts";
import { SELL_MULTIPLIER } from "../systems/EconomySystem.ts";
import { t, localizedName, getLocale } from "../utils/i18n.ts";
import { PANEL } from "../rendering/designTokens.ts";
import { TowerPreview, type PreviewDrawFn } from "./TowerPreview.ts";
import { deriveRamp, emissiveFill } from "../rendering/EmissiveMaterial.ts";
import { glowCache } from "../rendering/GlowSpriteCache.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { getStageFit, clientPointToStage } from "../utils/stage.ts";
import {
  UI_DURATION,
  UI_FONT_STACK,
  UI_SPACE,
  UI_TYPE,
  roleFontSize,
  withUiScale,
} from "./uiTokens.ts";
import { InfoTooltip } from "./InfoTooltip.ts";
import { isTouchTier } from "../utils/deviceTier.ts";

/** The firewall sentinel has no arena `drawNode`, so its bay draws a living
 *  green shield: a breathing lit hexagon (engine `emissiveFill`/`glowCache`)
 *  with scan-lines + a pulsing core — the same material language as the towers. */
const drawFirewallSpecimen =
  (color: string, colorLight: string): PreviewDrawFn =>
  (ctx, cx, cy, size, time): void => {
    const ramp = deriveRamp(color);
    const breathe = 0.9 + Math.sin(time * 2.4) * 0.1;
    const r = size * 0.34 * breathe;

    glowCache.draw(ctx, color, r * 1.5, cx, cy, 0.4);

    ctx.save();
    // Lit hexagon body (the §6.1 hot-core falloff).
    ctx.fillStyle = emissiveFill(ctx, cx, cy, r, ramp);
    ctx.beginPath();
    for (let j = 0; j < 6; j++) {
      const a = (j / 6) * Math.PI * 2 - Math.PI / 6;
      ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    }
    ctx.closePath();
    ctx.fill();

    // Scan-lines — the firewall reads as a shielded surface, not a flat shape.
    ctx.strokeStyle = colorLight;
    ctx.globalAlpha = 0.45;
    ctx.lineWidth = 0.6;
    for (let y = -r * 0.7; y < r * 0.7; y += 3.5) {
      ctx.beginPath();
      ctx.moveTo(cx - r * 0.62, cy + y);
      ctx.lineTo(cx + r * 0.62, cy + y);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Pulsing emissive core.
    glowCache.draw(ctx, colorLight, r * 0.3, cx, cy, 0.5);
    ctx.fillStyle = ramp.core;
    ctx.beginPath();
    ctx.arc(cx, cy, 2.2 + breathe, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

/**
 * Selection value emitted to the host:
 *  - an `Element`  → a tower of that element is selected
 *  - `"firewall"`  → firewall placement mode
 *  - `null`        → nothing selected (no ghost, no build) — the initial state
 */
export type TowerSelection = Element | "firewall" | null;
export type TowerSelectCallback = (type: TowerSelection) => void;

export interface CreditView {
  readonly currentGold: number;
  canAfford(cost: number): boolean;
}

export function upgradeButtonState(input: {
  upgradeCost: number | null;
  canAfford: boolean;
}): {
  disabled: boolean;
  stateClass: string;
  label: string;
} {
  if (input.upgradeCost == null) {
    return {
      disabled: true,
      stateClass: "tt-btn-maxed",
      label: t("tower.upgrade.max"),
    };
  }

  if (input.canAfford) {
    return {
      disabled: false,
      stateClass: "",
      label: t("tower.upgrade", { cost: input.upgradeCost }),
    };
  }

  return {
    disabled: true,
    stateClass: "tt-btn-unaffordable",
    label: t("tower.upgrade.insufficient", { cost: input.upgradeCost }),
  };
}

/** BL-205 — grace delay before the placed panel closes on mouse-leave. Short
 *  enough to feel immediate, long enough to bridge the tower→panel gap and
 *  sub-pixel jitter so the panel never closes while the cursor travels to a
 *  button (a re-enter cancels the pending close). */
export const TOWER_INFO_LEAVE_GRACE_MS = 120;

export const TOWER_ELEMENTS: readonly Element[] = ELEMENTS;
export const TOWER_KEYS = ["1", "2", "3", "4", "5", "6"];
export const TOWER_LABELS: Record<Element, string> = Object.fromEntries(
  ELEMENTS.map((el) => [el, ELEMENT_REGISTRY[el].label]),
) as Record<Element, string>;

export const TOWER_ROLES: Record<Element, string> = Object.fromEntries(
  ELEMENTS.map((el) => [el, ELEMENT_REGISTRY[el].role]),
) as Record<Element, string>;

/** Localized bay/tooltip name for a tower element (`tower.<el>`), falling
 *  back to the canonical `ELEMENT_REGISTRY` label (BL-113). Resolved lazily —
 *  module-scope constants would bake the pre-registration fallback in. */
export function towerLabel(el: Element): string {
  return localizedName("tower", el, TOWER_LABELS[el]);
}

/** Localized tooltip role tag for a tower element (`tower.<el>.role`). */
export function towerRole(el: Element): string {
  return localizedName("tower", `${el}.role`, TOWER_ROLES[el]);
}

/** BL-213 (S189) — the glanceable ONE-WORD role for the bay tooltip's impact
 *  head (DOT / CHAIN / BURST / SUPPORT / RAILGUN / AOE). The `tower.<el>.tag`
 *  key is registered for every element; the fallback (the leading token of the
 *  full role) is only a safety net for an unregistered locale. */
export function towerTag(el: Element): string {
  const role = towerRole(el);
  const fallback = (role.split(" / ")[0] ?? role).toUpperCase();
  return localizedName("tower", `${el}.tag`, fallback);
}

/** Render the player-facing description of a tower's special effect from its
 *  DATA — the ONE place a `special` becomes prose, shared by the build-hover
 *  card and the placed-tower panel. Every shipped special type MUST have a case
 *  here: the `default` raw-enum fallback is a BUG (it leaked literal `infection`
 *  / `railshot` to the player before D-262), and `towerTruth.test.ts` now fails
 *  if any tower's special has no real localized description. Exported (not a
 *  private method) so that net can cover it directly.
 *
 *  BL-174 / D-263 — `level` (default 1) makes the description LEVEL-AWARE: the
 *  dot/chain/burst copy names each tower's capability unlock so leveling reads as
 *  a decision, and the virus `infection` line now reads the LIVE per-level
 *  `VIRUS_LEVEL_PROFILE` (B4: an upgraded virus no longer describes its L1 zone).
 *  Level 1 is byte-identical to the pre-D-263 copy for the build-hover card. */
/** BL-182 / D-265 (S169) — the compact "active higher beats" suffix for the
 *  placed-panel special line: when an L4/L5 beat is live, name it inline (★NAME)
 *  so every active capability the runtime delivers is also EXPLAINED (the truth
 *  net: an unexplained capability is a B-class gap). The shared L3 beat is named
 *  by the `.on` copy already, so this only adds the new L4/L5 names. Empty below
 *  L4 (byte-identical to the pre-S169 build-hover card). */
function activeHigherBeats(el: string, level: number): string {
  const beats: string[] = [];
  if (level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 1) {
    const n = t(`tower.cap.${el}.l4.name`);
    if (n && n !== `tower.cap.${el}.l4.name`) beats.push(`★${n}`);
  }
  if (level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 2) {
    const n = t(`tower.cap.${el}.l5.name`);
    if (n && n !== `tower.cap.${el}.l5.name`) beats.push(`★${n}`);
  }
  // BL-175 / D-266 — at GOLD, name the element's signature mechanic inline so the
  // active capability the runtime delivers is also EXPLAINED (the truth net).
  if (level >= GOLD_TIER.LEVEL) {
    const n = t(`tower.cap.${el}.gold.name`);
    if (n && n !== `tower.cap.${el}.gold.name`) beats.push(`◆${n}`);
  }
  return beats.length ? ` · ${beats.join(" · ")}` : "";
}

/**
 * Write a stacked button label: everything before the first " · " is the title
 * line, the rest is the small line beneath it. Shared by the initial render and
 * the live affordability refresh so the two cannot drift — the refresh used to
 * be a plain `textContent` write, which silently collapsed the two lines.
 */
function setStackedButtonLabel(btn: HTMLElement, label: string): void {
  const [head, ...rest] = label.split(" · ");
  const tail = rest.join(" · ");
  btn.textContent = "";
  const t1 = document.createElement("span");
  t1.className = "tt-btn-t";
  t1.textContent = head ?? label;
  btn.appendChild(t1);
  if (tail) {
    const t2 = document.createElement("span");
    t2.className = "tt-btn-s";
    t2.textContent = tail;
    btn.appendChild(t2);
  }
}

/** One row of the compressed mechanic block: a keyword and its values. */
export interface MechRow {
  /** The keyword — a name, never a sentence opener. */
  k: string;
  /** The values, already localized and formatted. */
  v: string;
  /** True for a capability beat (rendered with the star, brighter). */
  star?: boolean;
  /** True for a beat that is NAMED but not yet unlocked (build-hover only). */
  locked?: boolean;
}

/**
 * special.type -> element. The mapping is 1:1 in `towers.json` and always has
 * been, so a caller should not have to know it: passing the element stays
 * possible, but omitting it must not silently drop the capability row. That is
 * exactly the bug the i18n tests caught when this parameter was introduced.
 */
const SPECIAL_ELEMENT: Record<string, string> = {
  dot: "fire",
  chain: "water",
  burst: "lightning",
  mark: "signal",
  railshot: "kinetic",
  infection: "virus",
};

/** Seconds, trimmed: 3000 -> "3", 500 -> "0.5" / "0,5" per locale. */
function secs(ms: number): string {
  const v = ms / 1000;
  const txt = (Math.round(v * 10) / 10).toString();
  return getLocale() === "de" ? txt.replace(".", ",") : txt;
}

/**
 * BL-293 (spec `2026-gameplay-ui-compression`) — the mechanic block, as ROWS.
 *
 * It used to be one sentence per tower, up to **240 characters in German**
 * (kinetic at L5), and it was the single largest block in the panel: 205.8 of
 * 765.3 design px. Measured, not estimated.
 *
 * Every number the sentence carried survives here; the framing around it does
 * not. `KEYWORD  values` reads in one fixation, and a row is short enough that
 * German — 23 % longer than English at this worst case — still fits one line.
 *
 * Two things were deliberately DROPPED, and both were derivable rather than
 * informative: railshot's trailing "Lv.3 pierces · Lv.4 corridor · Lv.5 twice"
 * (the capability rows and the NEXT card already say exactly that, so it was the
 * same fact three times), and the `1000/fireRate` cooldown that sat beside the
 * rate it was computed from.
 */
export function towerMechanicRows(
  s: Record<string, unknown> & { type: string },
  level = 1,
  element?: string,
  /** BL-204: the PLACED panel omits the locked forward tease because the
   *  feature card owns it. The build-hover keeps it — naming the beat before
   *  the player spends gold is the whole point of the D-262 truth net, and
   *  dropping it here was a real regression the tower-truth tests caught. */
  omitLockedTease = false,
): MechRow[] {
  const capUnlocked = level >= TOWER_CAPABILITY.UNLOCK_LEVEL;
  const rows: MechRow[] = [];
  const el = element ?? SPECIAL_ELEMENT[s.type] ?? "";
  const mech = (type: string, vars: Record<string, string | number>): void => {
    rows.push({ k: t(`tower.mech.${type}.k`), v: t(`tower.mech.${type}.v`, vars) });
  };

  switch (s.type) {
    case "dot": {
      const tickDmg = (s.tickDamage as number) ?? 0;
      const tickInt = (s.tickInterval as number) ?? 500;
      const duration = (s.duration as number) ?? 3000;
      const ticks = Math.floor(duration / tickInt);
      mech("dot", {
        dmg: tickDmg,
        int: secs(tickInt),
        dur: secs(duration),
        total: tickDmg * ticks,
      });
      break;
    }
    case "chain":
      mech("chain", {
        bounces: (s.bounces as number) ?? 2,
        decay: Math.round((((s.damageDecay as number) ?? 0.4) * 100)),
      });
      break;
    case "burst":
      mech("burst", {
        chance: Math.round((((s.critChance as number) ?? 0) * 100)),
        mult: (s.critMultiplier as number) ?? 2,
      });
      break;
    case "mark":
      mech("mark", {
        dur: secs((s.markDuration as number) ?? 3000),
        bonus: Math.round((((s.markDamageBonus as number) ?? 0) * 100)),
        slow: Math.round((1 - (((s.slowFactor as number) ?? 1))) * 100),
      });
      break;
    case "railshot":
      mech("railshot", { charge: secs((s.chargeTime as number) ?? 1500) });
      break;
    case "infection": {
      // B4 (D-263) — the LIVE per-level profile, never the static L1 `special`,
      // so an upgraded Contagion-Node describes the zone it actually delivers.
      const p = virusInfectionProfile(level);
      mech("infection", {
        radius: p.zoneRadius,
        tickDmg: p.tickDamage,
        int: secs(p.tickInterval),
        dur: secs(p.duration),
      });
      // The lingering pool is a property of the SPREAD, not a third mechanic —
      // giving it its own row cost 49 dp to say one number.
      const pool =
        p.hazardPoolDuration > 0
          ? ` · ${t("tower.mech.pool.v", { pool: secs(p.hazardPoolDuration) })}`
          : "";
      rows.push({
        k: t("tower.mech.spread.k"),
        v:
          t("tower.mech.spread.v", {
            chance: Math.round(p.spreadChance * 100),
            gen: p.maxGenerations,
            slow: Math.round((1 - p.speedDebuff) * 100),
          }) + pool,
      });
      break;
    }
    default:
      return [{ k: s.type, v: "" }];
  }

  // The L3 beat: the badge carries the NAME, the value one clause. Active gets
  // the star; locked gets its level instead, so the same row shape teaches both
  // "what this does now" and "what Lv.3 buys you" without a second sentence.
  if (el && (capUnlocked || !omitLockedTease)) {
    const name = t(`tower.cap.${el}.l3.name`);
    const val = t(`tower.capv.${el}`, {
      slow: Math.round((1 - (((s.chainSlowFactor as number) ?? 0.6))) * 100),
      period: secs((s.surgePeriodMs as number) ?? 2000),
      spread: Math.round(virusInfectionProfile(level).spreadChance * 100),
    });
    if (name && !name.startsWith("tower.cap.")) {
      const dup =
        !val.startsWith("tower.capv.") &&
        rows.some((r) => r.v && val && r.v.includes(val.split(" ")[0] ?? "\u0000"));
      rows.push({
        k: capUnlocked
          ? `★ ${name}`
          : `L${TOWER_CAPABILITY.UNLOCK_LEVEL} ${name}`,
        v: val.startsWith("tower.capv.") || dup ? "" : val,
        star: capUnlocked,
        locked: !capUnlocked,
      });
    }
  }

  // L4/L5/GOLD beats stay as the compact badges they already were. The row's
  // own star is the marker, so the per-name "★" prefix is stripped — "◆" is
  // kept, because it marks GOLD specifically and is not decoration.
  const higher = activeHigherBeats(el, level)
    .replace(/^ · /, "")
    .replace(/★/g, "");
  if (higher) rows.push({ k: "★", v: higher, star: true });

  return rows;
}

/**
 * The one-line form, for surfaces that take a string (the pre-placement build
 * hover). Kept so the compression of the PLACED panel does not silently change
 * what a player reads before they commit gold.
 */
export function describeTowerSpecial(
  s: Record<string, unknown> & { type: string },
  level = 1,
  omitLockedTease = false,
  element?: string,
): string {
  return towerMechanicRows(s, level, element, omitLockedTease)
    .map((r) => (r.v ? `${r.k}  ${r.v}` : r.k))
    .join(" · ");
}

/** BL-177 / D-263 — the honest, role-aware EFFECTIVE damage-per-second for a
 *  PLACED tower. The naive `damage × fireRate` is a defensible base number but it
 *  lies for the two outliers (§B3): the kinetic does NOT fire at `fireRate` (it
 *  runs an aim→charge→recover state machine), and the virus delivers almost all
 *  its damage through the infection zone, not the tiny `damage:6` impact. This is
 *  a single source so the placed panel can show what the tower truly delivers —
 *  including the level-gated capabilities. Returns 0 for a no-damage support tower. */
export function effectiveDps(tower: Tower): number {
  const s = tower.special;
  switch (tower.element) {
    case "kinetic": {
      // The state-machine cadence, not fireRate — plus the L5 double-shot.
      const cycleMs =
        (s?.aimTime ?? 250) + (s?.chargeTime ?? 1500) + (s?.recoverTime ?? 400);
      const shots = tower.level >= 5 ? 2 : 1;
      return (tower.damage * shots * 1000) / cycleMs;
    }
    case "virus": {
      // The dominant source: sustained zone tick damage to an infected target.
      const p = virusInfectionProfile(tower.level);
      return (p.tickDamage * 1000) / p.tickInterval;
    }
    case "fire": {
      // Base impact + the DoT sustained between shots (+ Scorch is situational AoE).
      let dps = tower.damage * tower.fireRate;
      if (s?.tickDamage && s?.tickInterval) {
        dps += (s.tickDamage * 1000) / s.tickInterval;
      }
      return dps;
    }
    case "lightning": {
      // Beam DPS × the expected crit uplift; the Overload surge makes it reliable.
      const critChance = s?.critChance ?? 0;
      const critMult = s?.critMultiplier ?? 2;
      const uplift = 1 + critChance * (critMult - 1);
      return tower.damage * tower.fireRate * uplift;
    }
    case "water": {
      // Base + the geometric chain (each bounce decayed by damageDecay). BL-182:
      // the L4 "Deep-Chill" extra bounce(s) add their decayed share once live —
      // a real per-cast DPS gain (the Flash-Freeze control value is not damage,
      // so it is honestly NOT folded into this DPS readout).
      const deepChill =
        tower.level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 1
          ? (s?.deepChillBounce ?? 0)
          : 0;
      const bounces = (s?.bounces ?? 0) + deepChill;
      const decay = s?.damageDecay ?? 0.6;
      let factor = 1;
      let d = 1;
      for (let i = 0; i < bounces; i++) {
        d *= decay;
        factor += d;
      }
      return tower.damage * tower.fireRate * factor;
    }
    default:
      return tower.damage * tower.fireRate;
  }
}

/** BL-182 / D-265 (S169) — the per-tower CAPABILITY ARC, as a level→beat map.
 *  The ONE place the kinetik-model arcs live for the transparency UI: each entry
 *  is the level a NAMED capability unlocks and its `tower.cap.<el>.<beat>.*` keys.
 *  Mirrors the gates wired in the combat systems (L3 = `TOWER_CAPABILITY.UNLOCK_
 *  LEVEL`, L4/L5 = +1/+2), so the panel's "next unlock" can never promise a beat
 *  the runtime won't deliver.
 *  GA-01 (Spec C+) — kinetic (armor-pierce L3 / pierce-corridor L4 / double-shot
 *  L5, `SniperCombat.ts:78/309/332`) and signal (honeypot detour L3,
 *  `BeamCombat.ts:379-381`) previously had ZERO entries below GOLD here, so
 *  `nextLevelCapability()`/`upcomingCapability()` returned `null` for every
 *  kinetic/signal level below GOLD even though the mechanics fire for real.
 *  Both now carry the same L3/L4/L5 arc shape as fire/water/lightning (signal
 *  stops at L3 — it has no L4/L5 damage beat, only its GOLD signature). */
const CAPABILITY_ARCS: Partial<Record<Element, Record<number, string>>> = {
  fire: {
    [TOWER_CAPABILITY.UNLOCK_LEVEL]: "fire",
    [TOWER_CAPABILITY.UNLOCK_LEVEL + 1]: "fire.l4",
    [TOWER_CAPABILITY.UNLOCK_LEVEL + 2]: "fire.l5",
    [GOLD_TIER.LEVEL]: "fire.gold",
  },
  water: {
    [TOWER_CAPABILITY.UNLOCK_LEVEL]: "water",
    [TOWER_CAPABILITY.UNLOCK_LEVEL + 1]: "water.l4",
    [TOWER_CAPABILITY.UNLOCK_LEVEL + 2]: "water.l5",
    [GOLD_TIER.LEVEL]: "water.gold",
  },
  lightning: {
    [TOWER_CAPABILITY.UNLOCK_LEVEL]: "lightning",
    [TOWER_CAPABILITY.UNLOCK_LEVEL + 1]: "lightning.l4",
    [TOWER_CAPABILITY.UNLOCK_LEVEL + 2]: "lightning.l5",
    [GOLD_TIER.LEVEL]: "lightning.gold",
  },
  virus: {
    [TOWER_CAPABILITY.UNLOCK_LEVEL]: "virus.l3",
    [GOLD_TIER.LEVEL]: "virus.gold",
  },
  // GA-01 (Spec C+) — kinetic's real L3/L4/L5 arc (armor-pierce / pierce
  // corridor / double-shot) + signal's real L3 (honeypot detour), now named
  // here so the next-unlock tease matches what the combat systems deliver.
  kinetic: {
    [TOWER_CAPABILITY.UNLOCK_LEVEL]: "kinetic",
    [TOWER_CAPABILITY.UNLOCK_LEVEL + 1]: "kinetic.l4",
    [TOWER_CAPABILITY.UNLOCK_LEVEL + 2]: "kinetic.l5",
    [GOLD_TIER.LEVEL]: "kinetic.gold",
  },
  signal: {
    [TOWER_CAPABILITY.UNLOCK_LEVEL]: "signal",
    [GOLD_TIER.LEVEL]: "signal.gold",
  },
};

/** BL-296 — a capability beat as the panel's COMPRESSED grammar: the level it
 *  lands on, the NAME, and one concrete effect clause. Never a sentence, never
 *  the word "active" about a beat the tower does not have yet. */
export interface CapBeat {
  /** The unlock level; `GOLD_TIER.LEVEL` for the signature. */
  level: number;
  /** The named beat, e.g. `CRYO-CHAIN` — English by owner decision (OQ-4). */
  name: string;
  /** The localized effect clause, value-first, no verb padding. */
  value: string;
  /** The GOLD signature, which is framed by TIER rather than by number. */
  gold?: boolean;
}

/** Map a capability key to its `CapBeat`. The shared L3 beats (fire/water/
 *  lightning/kinetic/signal) own their name in `tower.cap.<el>.l3.name` and
 *  their clause in `tower.capv.<el>` — the SAME pair the mechanic rows use, so
 *  a beat reads identically before and after it unlocks and the panel never
 *  duplicates a string. Returns null for an unnamed key.
 *
 *  BL-296 (build 97) — this used to reuse the ACTIVE `tower.cap.<el>.on` copy
 *  verbatim for the L3 beats, which put "★ CRYO-CHAIN active: bounced foes
 *  slowed 60%." under a kicker that reads NEXT, on a level-1 tower. The word was
 *  wrong and it was the only prose left in an otherwise keyword+value panel. */
function capabilityUnlockBeat(
  level: number,
  key: string,
  special?: Record<string, unknown> | null,
): CapBeat | null {
  // BL-175 / D-266 — the GOLD pinnacle beat (`<el>.gold`): framed as the coveted
  // GOLD signature, NOT a numbered level. Names/descs are the
  // `tower.cap.<el>.gold.{name,desc}` pair (EN+DE, parity-locked).
  if (key.endsWith(".gold")) {
    const name = t(`tower.cap.${key}.name`);
    if (!name || name === `tower.cap.${key}.name`) return null;
    return { level, name, value: t(`tower.cap.${key}.desc`), gold: true };
  }
  // The L3 shared beats. The numbers come from the tower's OWN special, through
  // the same arithmetic `towerMechanicRows` uses, so the beat reads identically
  // before and after it unlocks. (The retired `tower.cap.water.on` interpolated
  // a hardcoded 60 where `chainSlowFactor: 0.6` actually means −40 % speed — the
  // panel promised one number and the mechanic row showed another.)
  const SHARED_L3 = new Set(["fire", "water", "lightning", "kinetic", "signal"]);
  if (SHARED_L3.has(key)) {
    return {
      level,
      name: t(`tower.cap.${key}.l3.name`),
      value: t(`tower.capv.${key}`, {
        slow: Math.round((1 - ((special?.chainSlowFactor as number) ?? 0.6)) * 100),
        period: secs((special?.surgePeriodMs as number) ?? 2000),
      }),
    };
  }
  const name = t(`tower.cap.${key}.name`);
  if (!name || name === `tower.cap.${key}.name`) return null;
  return { level, name, value: t(`tower.cap.${key}.desc`) };
}

/** The flattened one-line form — `L3 · CRYO-CHAIN · bounced foes −60% speed`.
 *  Kept for callers and tests that want the beat as a single readable string;
 *  the panel renders the structured `CapBeat` instead. */
function capabilityUnlockLine(
  level: number,
  key: string,
  special?: Record<string, unknown> | null,
): string | null {
  const beat = capabilityUnlockBeat(level, key, special);
  if (!beat) return null;
  const lv = beat.gold ? t("tower.gold.badge") : t("tower.cap.lv", { level });
  return beat.value ? `${lv} · ${beat.name} · ${beat.value}` : `${lv} · ${beat.name}`;
}

/** BL-182 / D-265 (S169) — the capability the tower's NEXT level unlocks, as a
 *  ready-to-render line (owner steer: next level only). Null when the next level
 *  unlocks no named beat, or the tower is maxed / has no arc. Honest by
 *  construction: it reads the same `CAPABILITY_ARCS` the gates mirror. */
export function nextLevelCapability(tower: Tower): string | null {
  if (tower.level >= tower.maxLevel) return null;
  const arc = CAPABILITY_ARCS[tower.element];
  if (!arc) return null;
  const next = tower.level + 1;
  const key = arc[next];
  if (!key) return null;
  return capabilityUnlockLine(next, key, towerSpecial(tower));
}

/** The tower's special block as a plain record, for the beat arithmetic. */
function towerSpecial(tower: Tower): Record<string, unknown> | null {
  return (tower.special as unknown as Record<string, unknown>) ?? null;
}

/** BL-204 fix — the next capability milestone the tower is progressing TOWARD,
 *  looking ahead PAST level+1. A tower whose next named beat is a few levels off
 *  (e.g. a fire/DOT tower at L1 with SCORCH at L3) still teases that real beat
 *  instead of the panel defaulting to the GOLD crown at the very first upgrade
 *  (owner: "why is the crown shown at the first upgrade?"). Excludes the GOLD
 *  signature — the feature card has a dedicated GOLD state — so the crown surfaces
 *  only once every pre-GOLD beat is unlocked (i.e. at L5, the level before GOLD).
 *  Null when no pre-GOLD beat remains, the tower is maxed, or it has no arc. */
export function upcomingCapability(tower: Tower): string | null {
  const beat = upcomingCapabilityBeat(tower);
  if (!beat) return null;
  return capabilityUnlockLine(beat.level, beat.key, towerSpecial(tower));
}

/** The same lookahead as `upcomingCapability`, as the structured beat the panel
 *  renders (name and clause on their own rows, never joined into a sentence). */
export function upcomingCapabilityBeat(
  tower: Tower,
): (CapBeat & { key: string }) | null {
  if (tower.level >= tower.maxLevel) return null;
  const arc = CAPABILITY_ARCS[tower.element];
  if (!arc) return null;
  for (let lvl = tower.level + 1; lvl < GOLD_TIER.LEVEL; lvl++) {
    const key = arc[lvl];
    if (!key) continue;
    const beat = capabilityUnlockBeat(lvl, key, towerSpecial(tower));
    if (beat) return { ...beat, key };
  }
  return null;
}

/** The element's GOLD signature as a beat — the panel's pinnacle card, both
 *  while it is still locked (the pull) and once it is armed (the payoff). */
export function goldSignatureBeat(element: Element): CapBeat | null {
  const key = CAPABILITY_ARCS[element]?.[GOLD_TIER.LEVEL];
  if (!key) return null;
  return capabilityUnlockBeat(GOLD_TIER.LEVEL, key);
}

/**
 * BL-290 — the deploy bay's specimen size, in design px. `TowerPreview` writes
 * it as an inline style, so the CSS rule alone cannot set it; both read this
 * constant instead, which is what stops the two from drifting.
 */
/**
 * BL-290 — the specimen box is DERIVED from the live right-rail width, not from
 * a device check. The rail is itself aspect-driven (`computeRailWidths`), so an
 * ultrawide phone — which is exactly where the rail has surplus width and the
 * bays were unreadably small — gets the large specimen, while the 16:9/SE-class
 * and iPad rails keep today's 46 px and therefore today's vertical budget. That
 * is what stops the taller bays from overflowing the narrow rails, and it needs
 * no device-specific constant to do it.
 */
const BAY_PREVIEW_MIN = 46;
const BAY_PREVIEW_MAX = 60;
const BAY_PREVIEW_RATIO = 0.19;
function bayPreviewPx(): number {
  const rail = getStageFit().rightRail;
  if (!(rail > 0)) return BAY_PREVIEW_MIN;
  const raw = rail * BAY_PREVIEW_RATIO;
  return Math.round(
    raw < BAY_PREVIEW_MIN
      ? BAY_PREVIEW_MIN
      : raw > BAY_PREVIEW_MAX
        ? BAY_PREVIEW_MAX
        : raw,
  );
}

/**
 * BL-290 — the deploy bay's floor height, in design px. Chosen by MEASURING the
 * right rail's true worst case (seven bays + the four-chip payload rack: three
 * armed payloads plus Time-Warp) on all three device profiles until nothing
 * overflows, not by picking a round number. See the spec's budget table.
 *
 * BL-292 — briefly cut to 72 to make room for the payload rack's worst case,
 * then restored to 76, because the cut was paying the wrong bill. Measured on
 * the two notched profiles the clamp's middle term (`rail × 0.24`, rail at its
 * 323 dp ceiling → 77.5) overshoots the cap, so THIS value is the bay height
 * there: at 72 the bays really did render 4 dp shorter. What actually made the
 * four-chip worst case fit was the chip-name font clamp in `PayloadRackBar`,
 * and with that in place the rack fits at 76 on all three profiles — verified,
 * no overflow, no clipping. On the SE the rail is narrow enough that the middle
 * term wins (60.5 dp) and the bay is content-driven at 68 dp instead.
 */
const BAY_MIN_H = 76;

/**
 * BL-293 — the height of the two high-frequency actions, in design px.
 *
 * Deliberately the SAME as a deploy bay: that box measures ~33.2 real CSS px on
 * the primary review profile, which is the largest honest touch target this scaled
 * stage allows (AC-19/D-346 — 44 REAL px is geometrically impossible here and is
 * not claimed). Declared once because the base rule and the `pointer:coarse`
 * guard both need it; when those two drifted apart on the rails, the guard
 * silently became a cap.
 */
const TT_BTN_MIN_H = 76;

/** BL-297 — how long the armed SELL confirm stays armed with no further input.
 *  Long enough to read the button and commit, short enough that a stray tap can
 *  never leave a destructive control primed behind the operator's back. */
const SELL_ARM_TIMEOUT_MS = 4000;

export class TowerSelector {
  private container: HTMLDivElement;
  private towerData: Record<string, TowerConfig>;
  private selectedType_: TowerSelection = null;
  private onSelect: TowerSelectCallback | null;
  /** One living, lit specimen per bay (the armory headline). */
  private previews: TowerPreview[] = [];

  constructor(
    towerData: Record<string, TowerConfig>,
    onSelect: TowerSelectCallback,
  ) {
    this.towerData = towerData;
    this.onSelect = onSelect;
    this.container = document.createElement("div");
    this.container.id = "tower-selector";
    this.createPanel();
  }

  get selectedType(): TowerSelection {
    return this.selectedType_;
  }

  set selectedType(type: TowerSelection) {
    this.selectedType_ = type;
    this.updateSelection();
  }

  get element(): HTMLDivElement {
    return this.container;
  }

  /**
   * Commit a selection exactly as a bay click would — set state, repaint the
   * highlight, and notify the host (placement mode + ghost). Shared by all the
   * bay click handlers so selection drives ONE code path.
   */
  select(type: TowerSelection): void {
    this.selectedType_ = type;
    this.updateSelection();
    if (this.onSelect) this.onSelect(type);
  }

  updateGold(creditView: CreditView): void {
    const buttons =
      this.container.querySelectorAll<HTMLButtonElement>(".tower-btn");
    buttons.forEach((btn) => {
      const cost = parseInt(btn.dataset["cost"] ?? "0", 10);
      const wasUnaffordable = btn.classList.contains("unaffordable");
      const isUnaffordable = !creditView.canAfford(cost);
      btn.classList.toggle("unaffordable", isUnaffordable);
      // Affordability felt — the moment credits cross a tower's cost, the bay
      // value-flashes (§6.8 decay). Reduced-motion-safe (no flash, just lit).
      if (wasUnaffordable && !isUnaffordable) this.flashAffordable(btn);
    });
  }

  /** Alternates which of the two identical-keyframe classes carries the
   *  animation (see `flashAffordable`) — restarting via a NEW class the button
   *  doesn't already have avoids ever needing a forced-reflow read. PERF
   *  (verifier finding): this was a single INSTANCE field shared by all 7
   *  chips, so alternating flashes on DIFFERENT chips (X → Y → X) desynced a
   *  chip's own toggle from its own classes — a same-chip X → Y → X sequence
   *  within the flash window could land back on the class X already
   *  carried, making `classList.add` a silent no-op (the glint drops). Keyed
   *  per-button (WeakMap) instead, so each chip alternates independently. */
  private readonly affordFlashToggle = new WeakMap<HTMLButtonElement, boolean>();
  /** Per-flash generation counters, keyed per button — so an EARLIER flash's
   *  cleanup timeout can never strip the shared `tower-btn--afford-flash`
   *  marker class out from under a NEWER flash still running on the same
   *  button (the marker is what external readers key off of). */
  private readonly affordFlashGen = new WeakMap<HTMLButtonElement, number>();

  /** One-shot "just affordable" glint on a bay (the ProBar value-flash
   *  language). PERF (audit): this used to `void btn.offsetWidth` (a
   *  synchronous forced layout read) to restart the CSS animation — once per
   *  chip per affordability crossing, i.e. potentially several times a second
   *  across all 7 chips during combat, on the same frames as a drag. Instead,
   *  alternate between two classes (`-a`/`-b`) carrying IDENTICAL keyframes:
   *  adding a class the element does NOT already carry always (re)starts its
   *  animation, so no reflow read is needed. `tower-btn--afford-flash` itself
   *  stays a plain (non-animated) marker class for the flash's duration —
   *  external readers of that class name see no change. Do NOT reintroduce
   *  the forced reflow — that was the point of this change. */
  private flashAffordable(btn: HTMLButtonElement): void {
    if (isReducedMotion()) return;
    const toggled = !(this.affordFlashToggle.get(btn) ?? false);
    this.affordFlashToggle.set(btn, toggled);
    const active = toggled
      ? "tower-btn--afford-flash-a"
      : "tower-btn--afford-flash-b";
    const idle = toggled
      ? "tower-btn--afford-flash-b"
      : "tower-btn--afford-flash-a";
    const gen = (this.affordFlashGen.get(btn) ?? 0) + 1;
    this.affordFlashGen.set(btn, gen);
    btn.classList.remove(idle);
    btn.classList.add("tower-btn--afford-flash", active);
    window.setTimeout(() => {
      btn.classList.remove(active);
      // Only strip the shared marker if no newer flash started on this
      // button since — otherwise this cleanup would cut a newer flash short.
      if (this.affordFlashGen.get(btn) === gen) {
        btn.classList.remove("tower-btn--afford-flash");
      }
    }, UI_DURATION.flash + 60);
  }

  private createPanel(): void {
    // Section header
    const header = document.createElement("div");
    header.className = "ts-header";
    header.textContent = t("tower.header");
    this.container.appendChild(header);

    for (let i = 0; i < ELEMENTS.length; i++) {
      const type = ELEMENTS[i]!;
      const config = this.towerData[type]!;
      const btn = document.createElement("button");
      btn.className = `tower-btn tower-btn-${type}`;
      btn.dataset["type"] = type;
      btn.dataset["cost"] = String(config.cost);
      btn.style.setProperty("--tower-color", config.color);
      btn.style.setProperty("--tower-glow", config.colorLight);

      btn.innerHTML = `
        <div class="tower-info">
          <span class="tower-name">${towerLabel(type)}</span>
          <span class="tower-cost">\u00A2${config.cost}</span>
        </div>
        <span class="tower-key">${TOWER_KEYS[i]}</span>
      `;

      // \u2B50 Living specimen \u2014 the real arena `drawNode`, lit + bloomed in its bay.
      const preview = new TowerPreview({
        element: type,
        color: config.color,
        colorLight: config.colorLight,
        tier: 1,
        size: bayPreviewPx(),
      });
      btn.insertBefore(preview.el, btn.firstChild);
      this.previews.push(preview);

      // F-1/F-2/F-3 (D-332 prerequisite) — arming is now owned SOLELY by the
      // pointer pipeline (`bindPlacementPointer`'s pointerdown → `armChip` →
      // `armPlaceable` → `select()`), which applies the SAME `isInteractive()`/
      // `payloadActive()` guards the board itself uses. The browser fires a
      // synthetic `click` on this same button after every pointerup (mouse AND
      // touch) — if it ALSO called `select()`, a single tap armed through TWO
      // independent, unguarded mechanisms (arming mid-payload-aim, arming while
      // PAUSED). `event.detail` distinguishes the two origins: a POINTER-driven
      // click carries `detail >= 1` (the browser's click count) — a no-op here,
      // already handled. A KEYBOARD activation (Enter/Space on a focused chip)
      // carries `detail === 0` (no preceding pointer press ever reached the
      // pipeline) — still arms here, so keyboard/a11y users are unaffected.
      btn.addEventListener("click", (e) => {
        if (e.detail === 0) this.select(type);
        // P1-4: touch has no hover — surface the bay's stats on tap (auto-hides).
        this.touchPreviewTooltip(() => this.showTooltip(btn, type, config));
      });

      btn.addEventListener("mouseenter", () => {
        preview.setHover(true);
        this.showTooltip(btn, type, config);
      });
      btn.addEventListener("mouseleave", () => {
        preview.setHover(false);
        this.hideTooltip();
      });

      this.container.appendChild(btn);
      preview.mount();
    }

    // Firewall button
    const fwBtn = document.createElement("button");
    fwBtn.className = "tower-btn tower-btn-firewall";
    fwBtn.dataset["type"] = "firewall";
    fwBtn.dataset["cost"] = String(FIREWALL_DEFAULTS.cost);
    const fwColor = COLORS.SENTINEL_GREEN;
    const fwGlow = "#7FFF7F";
    fwBtn.style.setProperty("--tower-color", fwColor);
    fwBtn.style.setProperty("--tower-glow", fwGlow);
    fwBtn.innerHTML = `
      <div class="tower-info">
        <span class="tower-name">${t("tower.firewall")}</span>
        <span class="tower-cost">\u00A2${FIREWALL_DEFAULTS.cost}</span>
      </div>
      <span class="tower-key">7</span>
    `;

    const fwPreview = new TowerPreview({
      color: fwColor,
      colorLight: fwGlow,
      draw: drawFirewallSpecimen(fwColor, fwGlow),
      size: bayPreviewPx(),
    });
    fwBtn.insertBefore(fwPreview.el, fwBtn.firstChild);
    this.previews.push(fwPreview);

    // F-1/F-2/F-3 — see the tower-bay `click` listener above for the full
    // rationale; same `detail` guard (pointer-origin no-ops, keyboard arms).
    fwBtn.addEventListener("click", (e) => {
      if (e.detail === 0) this.select("firewall");
      this.touchPreviewTooltip(() => this.showFirewallTooltip(fwBtn));
    });
    fwBtn.addEventListener("mouseenter", () => {
      fwPreview.setHover(true);
      this.showFirewallTooltip(fwBtn);
    });
    fwBtn.addEventListener("mouseleave", () => {
      fwPreview.setHover(false);
      this.hideTooltip();
    });
    this.container.appendChild(fwBtn);
    fwPreview.mount();

    const towerRow = document.getElementById("tower-row");
    (towerRow ?? document.body).appendChild(this.container);
    this.updateSelection();
    this.injectStyles();
    window.addEventListener("resize", this.onStageResize);
    window.addEventListener("orientationchange", this.onStageResize);
  }

  /**
   * BL-290 — re-derive every specimen box from the CURRENT right-rail width.
   * The rail is aspect-driven, so a rotation (or an iOS toolbar collapse, which
   * re-fits the stage) changes it; without this the boxes would keep whatever
   * size they were built at and drift from the CSS rule that sizes their frame.
   */
  private readonly onStageResize = (): void => {
    const px = bayPreviewPx();
    for (const p of this.previews) p.setSize(px);
  };

  destroy(): void {
    window.removeEventListener("resize", this.onStageResize);
    window.removeEventListener("orientationchange", this.onStageResize);
    for (const p of this.previews) p.destroy();
    this.previews = [];
  }

  private updateSelection(): void {
    const buttons =
      this.container.querySelectorAll<HTMLButtonElement>(".tower-btn");
    buttons.forEach((btn) => {
      const btnType = btn.dataset["type"];
      const isFirewall = btnType === "firewall";
      btn.classList.toggle(
        "selected",
        isFirewall
          ? this.selectedType_ === "firewall"
          : btnType === this.selectedType_,
      );
    });
  }

  private injectStyles(): void {
    const style = document.createElement("style");
    style.textContent = `
      #tower-selector {
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 0 0 ${UI_SPACE.xxs}px;
      }
      /* BL-62 — section header harmonised to the signed mockup .secthead
         (the muted #6f8a72 "// DEPLOY" rhythm + the #0e2614 rule).
         Right-rail-headroom correction (docs/decisions.md D-335, amended):
         header padding tightened xxs(4px)/hair(2px), fixed design-px so the
         saving is identical at every --chrome-scale. */
      .ts-header {
        padding: ${UI_SPACE.xxs}px 12px ${UI_SPACE.hair}px;
        font-size: ${roleFontSize("micro")};
        color: #6f8a72;
        letter-spacing: 2px;
        text-transform: uppercase;
        border-bottom: 1px solid #0e2614;
      }
      /* BL-62 — carded deploy bays matching the signed mockup .twr: a framed,
         rounded row on a faint lit ground that border-glows + lifts in the
         tower's own colour on hover (was a flat, bottom-ruled list item). */
      /* Right-rail-headroom correction (docs/decisions.md D-335, amended): a
         few px trimmed from each of the 7 bays' vertical margin/padding is 7x
         the saving (the preview canvas, untouched at 46x46, still sets the
         bay's legibility floor). Fixed design-px, so identical at every
         --chrome-scale. */
      /* BL-290 — the bays get their real size back.
         Measured before this change on the 852×393 target (stage scale
         0.436667): a bay was 56 design px = **24.5 REAL css px**, against the
         44 px floor knowledge/system/ios-platform.md mandates. The
         min-height:44px below looked like compliance but is 44 *design* px
         INSIDE the scaled stage — the exact "large design-px literal inside the
         stage" that document warns against. Seven bays are on screen for the
         whole run; they were the smallest touch targets in the game.
         The height comes from the right rail's own surplus (it had ~270 dp of
         unused height) plus the two-up payload rack, NOT from the board. */
      .tower-btn {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: ${UI_SPACE.xxs}px 8px 0;
        padding: 7px 9px;
        min-height: clamp(56px, calc(var(--right-panel-width) * 0.24), ${BAY_MIN_H}px);
        box-sizing: border-box;
        /* BL-290 — a taller bay needs a stronger GROUND, not more glow. The
           slab reads as a seated hardware bay: a faint top-lit face over the
           panel, one hairline frame, and a slightly deeper radius to match the
           bigger specimen well. Hierarchy from material, not from bloom. */
        background: linear-gradient(180deg, rgba(255,255,255,0.035), rgba(0,0,0,0.18));
        border: 1px solid #16351d;
        border-radius: 9px;
        cursor: pointer;
        transition: border-color 0.12s ease, box-shadow 0.12s ease,
                    transform 0.12s ease, background 0.12s ease;
        position: relative;
        font-family: ${UI_FONT_STACK};
      }
      .tower-btn:hover {
        border-color: var(--tower-color);
        box-shadow: 0 0 12px -2px var(--tower-color);
        transform: translateX(-2px);
      }
      .tower-btn.selected {
        border-color: var(--tower-glow);
        background: linear-gradient(180deg, rgba(136, 224, 208,0.09), transparent);
        /* BL-207 (S189) — stronger active-state read: a 3px lit left accent in
           the bay's own glow so the selected bay is unmistakable ABOVE the
           dimmed (opacity .32) unaffordable bays on a dense iPhone rail. */
        box-shadow: inset 3px 0 0 var(--tower-glow), 0 0 16px -2px var(--tower-glow), inset 0 0 12px rgba(136, 224, 208,0.06);
      }
      .tower-btn.selected .tower-preview-canvas {
        filter: drop-shadow(0 0 6px var(--tower-color));
      }
      /* BL-236 — unaffordable bays stay legible (you can read the whole menu)
         but clearly CAN'T-AFFORD: a touch dimmer + the cost turns warning-red so
         affordability is colour-coded, not just an overall fade. The selected
         bay's 3px lit accent + glow still reads above these. */
      .tower-btn.unaffordable {
        opacity: 0.46;
        pointer-events: none;
      }
      .tower-btn.unaffordable .tower-cost {
        color: ${COLORS.ORACLE_RED};
      }
      /* The lit-CRT bay holding the living specimen. BL-290 — 46 → 70 px: the
         specimen is what distinguishes one bay from another at a glance during
         a wave, and at 46 dp it read as 20 REAL px on the target phone. This is
         the single change that does most for "which tower is this?". */
      .tower-preview-canvas {
        width: clamp(${BAY_PREVIEW_MIN}px, calc(var(--right-panel-width) * ${BAY_PREVIEW_RATIO}), ${BAY_PREVIEW_MAX}px);
        height: clamp(${BAY_PREVIEW_MIN}px, calc(var(--right-panel-width) * ${BAY_PREVIEW_RATIO}), ${BAY_PREVIEW_MAX}px);
        flex-shrink: 0;
        border-radius: 8px;
      }
      /* Affordability felt — a one-shot green glint on the bay + cost the moment
         a tower becomes affordable (the §6.8 ProBar value-flash language).
         PERF: the animation lives on the ALTERNATING -a/-b pair (identical
         keyframes) so flashAffordable can restart it without a forced-reflow
         read; .tower-btn--afford-flash itself stays a plain marker class. */
      .tower-btn--afford-flash-a,
      .tower-btn--afford-flash-b {
        animation: towerAffordFlash ${UI_DURATION.flash}ms ${"cubic-bezier(0.16,1,0.3,1)"};
      }
      .tower-btn--afford-flash-a .tower-cost,
      .tower-btn--afford-flash-b .tower-cost {
        animation: towerCostFlash ${UI_DURATION.flash}ms ${"cubic-bezier(0.16,1,0.3,1)"};
      }
      @keyframes towerAffordFlash {
        0% { box-shadow: inset 0 0 0 transparent; background: rgba(136, 224, 208,0.18); }
        100% { box-shadow: inset 0 0 0 transparent; background: transparent; }
      }
      @keyframes towerCostFlash {
        0% { color: #ffffff; text-shadow: 0 0 10px ${RARITY.legendary.mid}; }
        100% { color: ${RARITY.legendary.mid}; text-shadow: none; }
      }
      @media (prefers-reduced-motion: reduce) {
        .tower-btn--afford-flash-a,
        .tower-btn--afford-flash-b,
        .tower-btn--afford-flash-a .tower-cost,
        .tower-btn--afford-flash-b .tower-cost { animation: none; }
      }
      .tower-info {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        /* BL-290 — the bay inherited text-align:center from the rail, which put
           each name in a different place depending on its length. With seven
           bays stacked that reads as noise. Left-aligning against the specimen
           gives the rail ONE type column: the eye scans a straight edge instead
           of re-finding the text on every row. */
        text-align: left;
        /* BL-207 (S189) — let the text column SHRINK and reserve room for the
           absolute .tower-key badge, so a long bay name (Rail-Sentinel /
           Contagion-Node) grown by the phone --chrome-scale (up to 1.5x) can
           never slide under the key or force the bay to overflow/clip. */
        min-width: 0;
        padding-right: 14px;
      }
      .tower-name {
        font-size: ${roleFontSize("label")};
        color: var(--tower-color);
        letter-spacing: 1px;
        text-transform: uppercase;
        font-weight: bold;
        /* BL-207 (S189) — clean wrap for the worst-case long EN/DE bay names
           inside the constrained, chrome-scaled column (no mid-word spill,
           mirrors the .tt-name/.tt-role readability-net convention). */
        overflow-wrap: break-word;
        word-break: normal;
      }
      /* BL-62 — the signed mockup's gold cost. BL-260: sourced from the central
         RARITY.legendary.mid token (Glacier gold) so it tracks the palette
         instead of a stale #FFC23D literal that drifted off-identity. */
      /* BL-290 — the cost is the SECOND read after the specimen ("can I afford
         it?"), so it steps up from micro to the numeric role and gains tabular
         figures: a column of prices that don't jitter between bays. */
      .tower-cost {
        font-size: ${withUiScale(UI_TYPE.numeric.size - 3)};
        color: ${RARITY.legendary.mid};
        font-variant-numeric: tabular-nums;
        letter-spacing: 0.4px;
      }
      .tower-key {
        position: absolute;
        top: 4px;
        right: 6px;
        font-size: ${roleFontSize("micro")};
        color: #335533;
        font-family: ${UI_FONT_STACK};
      }
      .tower-tooltip {
        position: fixed;
        z-index: 9999;
        background: #0a140a;
        border: 1px solid #1a4a1a;
        padding: 8px 10px;
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("micro")};
        color: #b0ffb0;
        pointer-events: none;
        max-width: 220px;
        box-shadow: 0 0 12px rgba(136, 224, 208,0.15);
        line-height: 1.6;
        /* D-262 readability fix: the long special / upgrade-preview lines (worst
           case the DE compounds) wrap cleanly inside the 220px panel instead of
           spilling over the tower window. break-word only breaks an unfittable
           token; normal words still wrap at spaces (no mid-word breaks). */
        overflow-wrap: break-word; word-break: normal;
      }
      /* BL-203 — the placed-panel headline wraps inside the box too: the name +
         role (worst case the DE role compound) must never spill past the panel
         edge. Explicit guard on the headline (not just the container) so the
         readability net can assert the headline itself is wrap-contained. */
      .tower-tooltip .tt-name { color: var(--tt-color, ${COLORS.SENTINEL_GREEN}); font-weight: bold; font-size: ${roleFontSize("label")}; overflow-wrap: break-word; word-break: normal; }
      .tower-tooltip .tt-role { color: var(--tt-color, ${COLORS.SENTINEL_GREEN}); opacity: 0.7; font-style: italic; font-size: ${roleFontSize("micro")}; margin-bottom: 3px; overflow-wrap: break-word; word-break: normal; }
      /* BL-204 — the active special (the runtime's current capability, kept for
         honesty) + synergy read as quiet lines under the stats; the next-level
         capability beat (BL-182) now lives in the structured feature card below. */
      .tower-tooltip .tt-special { color: ${COLORS.ICE_CYAN}; margin-top: 6px; font-size: ${roleFontSize("micro")}; font-style: italic; opacity: 0.95; overflow-wrap: break-word; word-break: normal; }
      .tower-tooltip .tt-synergy { color: ${COLORS.ICE_CYAN}; margin-top: 6px; font-size: ${roleFontSize("micro")}; opacity: 0.92; }
      /* GA-18: the synergy DESCRIPTION line. Dimmer + indented so the names
         stay the primary read and the explanations sit under them. */
      .tower-tooltip .tt-synergy-desc { color: ${COLORS.ICE_CYAN}; margin-left: 8px; font-size: ${roleFontSize("micro")}; opacity: 0.66; }
      /* BL-175 / D-266 — the GOLD pinnacle badge: the NAMED "GOLD" tier crest in the
         legendary apex material (RARITY.legendary.mid), set apart from the numbered
         Lv.N read. The element-true LOOK lives on the tower node (the crown/aura);
         this is the panel's coveted label. */
      .tower-tooltip .tt-gold-badge {
        color: ${RARITY.legendary.mid};
        font-weight: bold;
        letter-spacing: 1px;
        text-shadow: 0 0 4px ${RARITY.legendary.mid};
      }
      .tower-info-panel--gold .tt-name { text-shadow: 0 0 6px ${RARITY.legendary.mid}; }
      /* BL-204 — the placed upgrade/sell panel: a PREMIUM, consistent-size card
         (owner: the flat text panel read "too loveless… even hard to read").
         Bloons-informed but on OUR L1→L5→GOLD arc — a tier ladder, stat rows with
         deltas, the next ★ capability as a milestone card, the GOLD pinnacle
         teased — all on the unified lit-panel material (S177/D-272). It is ALWAYS
         the SAME SIZE (owner ask): a fixed width + a --ui-scale-aware MIN-height
         (a floor that grows-never-clips — the BL-206 lesson), flex-column with the
         actions bottom-pinned and the feature slot ALWAYS populated, so every
         tower / level / state reads at an identical, stable size. */
      .tower-info-panel {
        pointer-events: auto;
        box-sizing: border-box;
        max-width: none;
        width: ${withUiScale(236)};
        min-height: ${withUiScale(120)};
        display: flex;
        flex-direction: column;
        padding: ${withUiScale(4)};
        border: 1px solid transparent;
        border-radius: 11px;
        line-height: 1.45;
        background:
          radial-gradient(140% 120% at 50% -10%, color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 12%, transparent), transparent 55%),
          linear-gradient(180deg, #10201d 0%, #0b1518 58%, #081013 100%);
        box-shadow:
          inset 0 1px 0 color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 20%, transparent),
          inset 0 0 0 1px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 15%, transparent),
          0 0 22px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 8%, transparent),
          0 20px 48px -22px #000;
      }
      /* header: name + role on the left, the tier ladder pinned top-right */
      .tt-head { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
      .tt-role { margin-left: auto; text-align: right; flex: 0 1 auto; min-width: 0;
                 overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .tt-ladderrow { display: flex; margin: 2px 0 4px; }
      .tt-headtext { min-width: 0; }
      .tt-ladder { display: flex; align-items: center; gap: 4px; flex: none; margin-top: 3px; }
      .tt-pip {
        width: 7px; height: 7px; border-radius: 2px;
        background: color-mix(in srgb, ${COLORS.SENTINEL_GREEN_DIM} 38%, transparent);
        box-shadow: inset 0 0 0 1px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 22%, transparent);
      }
      .tt-pip-on {
        background: linear-gradient(180deg, color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 55%, #fff), ${COLORS.SENTINEL_GREEN});
        box-shadow: 0 0 6px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 70%, transparent);
      }
      .tt-pip-cur { transform: scale(1.34); }
      .tt-crown { width: 15px; height: 15px; margin-left: 3px; opacity: 0.34; filter: grayscale(0.3); }
      .tower-info-panel--gold .tt-crown { opacity: 1; filter: none; }
      .tt-rule { height: 1px; margin: 0; background: linear-gradient(90deg, transparent, color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 24%, transparent), transparent); }
      /* stat rows: dim role label · value · (delta on upgrade preview) */
      /* ── BL-293 · the fixed stat grid ──────────────────────────────────
         Cell ORDER never varies by tower, so the eye learns one position and
         does not re-read the labels every time a panel opens. Change is carried
         by the chip + full brightness; no change by the dimmed "hold" state and
         a quiet dash. An arrow in this panel means gain, and only gain. */
      .tt-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
      .tt-cell {
        display: flex; align-items: baseline; gap: 6px;
        background: color-mix(in srgb, ${PANEL.BG} 88%, black);
        border: 1px solid ${PANEL.BORDER}; border-radius: 4px;
        padding: 4px 5px; min-width: 0; overflow: hidden;
      }
      .tt-hold {
        background: color-mix(in srgb, ${PANEL.BG} 60%, black);
        border-color: color-mix(in srgb, ${PANEL.BORDER} 50%, transparent);
      }
      .tt-cl {
        color: ${COLORS.TEXT_SECONDARY}; font-size: ${roleFontSize("micro")};
        letter-spacing: 0.1em; flex: 0 0 auto;
      }
      .tt-hold .tt-cl { color: color-mix(in srgb, ${COLORS.TEXT_SECONDARY} 62%, transparent); }
      /* BL-293 — the grid value uses the LABEL role, not HEADING. The frozen mockup
         sized this in raw design px (24 dp); the shipped type system multiplies by
         --ui-scale × --chrome-scale, which on the phone tier turns role "heading"
         (20) into ~34 dp and overflowed every two-column cell. Role "label" (14)
         lands at ~24 dp — the size the mockup was actually judged at. */
      .tt-cv {
        color: ${COLORS.TEXT_PRIMARY}; font-size: ${roleFontSize("label")};
        line-height: 1; white-space: nowrap;
      }
      .tt-hold .tt-cv { color: color-mix(in srgb, ${COLORS.TEXT_SECONDARY} 80%, transparent); font-size: ${roleFontSize("caption")}; }
      .tt-cu { color: ${COLORS.TEXT_SECONDARY}; font-size: ${roleFontSize("micro")}; }
      .tt-chip {
        margin-left: auto; color: ${PANEL.BG}; background: ${COLORS.SENTINEL_GREEN};
        border-radius: 3px; padding: 0 4px; font-size: ${roleFontSize("micro")};
        white-space: nowrap;
      }
      .tt-dash { margin-left: auto; color: color-mix(in srgb, ${COLORS.TEXT_SECONDARY} 50%, transparent); font-size: ${roleFontSize("micro")}; }

      /* ── the mechanic rows — a keyword and its values, never a sentence ── */
      .tt-mech {
        display: flex; align-items: baseline; gap: 7px; margin-top: 3px;
        padding: 3px 7px; border-radius: 0 4px 4px 0;
        background: color-mix(in srgb, ${PANEL.BG_ELEVATED} 55%, black);
        border-left: 3px solid ${COLORS.ICE_CYAN};
      }
      .tt-mech-quiet {
        border-left-color: color-mix(in srgb, ${COLORS.ICE_CYAN} 30%, transparent);
        background: color-mix(in srgb, ${PANEL.BG} 70%, black);
      }
      .tt-mk {
        color: ${COLORS.ICE_CYAN}; font-size: ${roleFontSize("micro")};
        letter-spacing: 0.08em; flex: 0 0 auto;
      }
      .tt-mech-quiet .tt-mk { color: ${COLORS.TEXT_SECONDARY}; }
      .tt-mv {
        color: ${COLORS.TEXT_SECONDARY}; font-size: ${roleFontSize("micro")};
        margin-left: auto; text-align: right; overflow-wrap: break-word; min-width: 0;
      }

      /* ── the synergy line — ONE line, name then effect (OQ-1) ─────────── */
      .tt-syn {
        display: flex; align-items: baseline; gap: 5px; margin-top: 3px;
        font-size: ${roleFontSize("micro")}; color: ${COLORS.TEXT_SECONDARY};
        min-width: 0;
      }
      .tt-syn b { color: ${COLORS.SENTINEL_GREEN}; font-weight: normal; flex: 0 0 auto; }
      .tt-syn-dot { color: ${COLORS.SENTINEL_GREEN_DIM}; }
      .tt-syn-v { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .tt-eff { color: ${COLORS.ICE_CYAN}; font-size: ${roleFontSize("micro")}; margin-top: 4px; letter-spacing: 0.2px; }
      .tt-support-line { color: ${COLORS.ICE_CYAN}; font-style: italic; }
      /* feature card — ALWAYS present (the next ★ capability, the GOLD signature,
         or the GOLD invitation when maxed); grows to fill so the panel size is
         stable across states. */
      .tt-feat {
        display: flex; gap: 8px; align-items: center; flex: 0 0 auto;
        margin-top: 4px; padding: 4px 8px; border-radius: 6px;
        background: linear-gradient(180deg, color-mix(in srgb, ${COLORS.ICE_CYAN} 11%, transparent), color-mix(in srgb, ${COLORS.ICE_CYAN} 3%, transparent));
        box-shadow: inset 0 0 0 1px color-mix(in srgb, ${COLORS.ICE_CYAN} 24%, transparent);
      }
      .tt-feat-emblem { width: 18px; height: 18px; flex: none; filter: drop-shadow(0 0 5px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 55%, transparent)); }
      .tt-feat-body { min-width: 0; display: flex; flex-direction: column; gap: 1px; flex: 1 1 auto; }
      /* BL-296 — the IDENTITY row: kicker, the unlock level, then the NAME. The
         name is the loud element because it is what the player is buying; the
         level is a quiet amber chip so "how far off" reads without a sentence. */
      .tt-feat-head { display: flex; align-items: baseline; gap: 6px; min-width: 0; }
      .tt-feat-kicker { font-size: ${roleFontSize("micro")}; letter-spacing: 1.3px; flex: 0 0 auto; text-transform: uppercase; color: ${COLORS.ICE_CYAN}; }
      .tt-feat-lv {
        font-size: ${roleFontSize("micro")}; flex: 0 0 auto; color: ${COLORS.AMBER_WARN};
        border: 1px solid color-mix(in srgb, ${COLORS.AMBER_WARN} 45%, transparent);
        border-radius: 3px; padding: 0 3px; line-height: 1.15;
      }
      .tt-feat-name {
        font-size: ${roleFontSize("caption")}; color: ${COLORS.TEXT_PRIMARY};
        letter-spacing: 0.04em; min-width: 0; overflow: hidden; text-overflow: ellipsis;
        white-space: nowrap;
      }
      .tt-feat-text { font-size: ${roleFontSize("micro")}; color: ${COLORS.TEXT_SECONDARY}; line-height: 1.2; overflow-wrap: break-word; word-break: normal; }
      .tt-feat--gold, .tower-info-panel--gold .tt-feat {
        background: linear-gradient(180deg, color-mix(in srgb, ${RARITY.legendary.mid} 16%, transparent), color-mix(in srgb, ${RARITY.legendary.mid} 4%, transparent));
        box-shadow: inset 0 0 0 1px color-mix(in srgb, ${RARITY.legendary.mid} 30%, transparent);
      }
      .tt-feat--gold .tt-feat-kicker, .tower-info-panel--gold .tt-feat-kicker { color: ${RARITY.legendary.mid}; }
      /* still locked: the pinnacle is NAMED (D-262 — the name must be readable
         before it is bought) but sits quiet until the tower actually reaches it. */
      .tt-feat--gold:not(.tt-feat--armed) .tt-feat-name { color: color-mix(in srgb, ${COLORS.TEXT_PRIMARY} 62%, transparent); }
      .tt-feat--gold:not(.tt-feat--armed) .tt-feat-emblem { opacity: 0.62; }
      .tt-feat--armed .tt-feat-name { color: ${RARITY.legendary.mid}; }
      .tt-feat-emblem--gold { filter: drop-shadow(0 0 5px color-mix(in srgb, ${RARITY.legendary.mid} 60%, transparent)); }
      /* ── BL-293 · the two high-frequency actions ───────────────────────
         Same height, deliberately. The owner asked that Sell stay a full,
         large touch target rather than a subordinate text button, so it is
         secondary through TREATMENT — flat, unlit, its own box — never through
         being smaller. The gap is a real one: 3.06 real px was measured between
         them before this change, with Sell being the destructive action. */
      .tt-actions { display: flex; gap: 18px; margin-top: 5px; }
      .tt-btn {
        display: flex; flex-direction: column; align-items: center;
        justify-content: center; gap: 1px; line-height: 1.1;
        min-height: ${TT_BTN_MIN_H}px; border-radius: 5px; cursor: pointer;
        font-family: ${UI_FONT_STACK}; letter-spacing: 0.05em;
        border: 1px solid transparent; padding: 0 5px;
      }
      /* BL-297 (owner HUMAN-Befund on build 97) — ACTION TYPOGRAPHY, sized
         against the PRODUCTION render rather than the mockup. Measured before:
         the label rendered 10.34 REAL px and the price 8.86 REAL px with
         letter-spacing 2.43px — a third of a glyph between the digits of a
         three-character price. The boxes were 93 x 33.2 and 66.6 x 33.2 real px:
         a large touch target carrying microscopic content, which is exactly what
         the owner reported. Label -> heading role, price -> label role; tracking on
         the price drops from 0.12em to 0.05em so it reads as ONE token. */
      .tt-btn-t { font-size: ${roleFontSize("heading")}; white-space: nowrap; }
      .tt-btn-s { font-size: ${roleFontSize("label")}; letter-spacing: 0.05em; opacity: 0.92; white-space: nowrap; }
      /* The width split carries a little of the primary read, but only a little —
         the fill and the glow carry most of it. It is set so the ARMED sell label
         (the longest string either action ever holds: DE "BESTÄTIGEN") clears its
         box with real headroom instead of the 9 dp it had at 1.75/1.25. */
      .tt-btn-upgrade { flex: 1.55; }
      .tt-btn-sell {
        flex: 1.45;
        background: color-mix(in srgb, ${COLORS.ORACLE_RED} 12%, ${PANEL.BG});
        border-color: color-mix(in srgb, ${COLORS.ORACLE_RED} 55%, transparent);
        color: ${COLORS.ORACLE_RED};
      }
      .tt-btn-sell:hover {
        background: color-mix(in srgb, ${COLORS.ORACLE_RED} 16%, ${PANEL.BG});
        border-color: ${COLORS.ORACLE_RED};
      }
      .tt-btn-upgrade:not([disabled]) {
        color: #07140f;
        border-color: transparent;
        background: linear-gradient(180deg, color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 55%, #fff), ${COLORS.SENTINEL_GREEN} 70%, color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 80%, #000));
        box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 0 16px color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 35%, transparent), 0 8px 18px -10px #000;
      }
      .tt-btn-upgrade:not([disabled]):hover { filter: brightness(1.08); }
      .tt-btn[disabled] { opacity: 0.85; cursor: not-allowed; color: #8aa78a; }
      .tt-btn-maxed[disabled] {
        color: ${COLORS.AMBER_WARN};
        border-color: #806a12;
        background: #1a1608;
        opacity: 1;
      }
      .tt-btn-unaffordable[disabled] {
        color: #FF8F8F;
        border-color: #6a3030;
        background: #1e0d0d;
      }
      /* BL-297 — a SECOND .tt-btn-sell rule used to re-declare a near-black
         #4a1a1a border here, overriding the token-derived one above and leaving
         a destructive action outlined at almost no contrast. Secondary is carried
         by TREATMENT (flat box vs the primary's lit gradient), never by making
         the control hard to see. */
      .tt-btn-sell:hover { background: color-mix(in srgb, ${COLORS.ORACLE_RED} 20%, ${PANEL.BG}); border-color: ${COLORS.ORACLE_RED}; }
      /* BL-119 / BL-297 — the SELL button ARMED as the confirm, IN PLACE: same
         element, same slot, same two-line shape (label over price), a brighter
         destructive read so the second press is unmistakable. The owner's rule is
         that the confirm lives in the button — no second dialog inside the
         dialog, no CANCEL block, no refund percentage, no path-warning line. */
      .tt-btn-sell-armed {
        background: linear-gradient(180deg, color-mix(in srgb, ${COLORS.ORACLE_RED} 34%, ${PANEL.BG}), color-mix(in srgb, ${COLORS.ORACLE_RED} 20%, ${PANEL.BG}));
        border-color: ${COLORS.ORACLE_RED};
        color: #fff;
        box-shadow: 0 0 12px color-mix(in srgb, ${COLORS.ORACLE_RED} 45%, transparent),
                    inset 0 0 0 1px color-mix(in srgb, ${COLORS.ORACLE_RED} 55%, transparent);
      }
      /* BL-109 — touch-target floor (the project's pointer:coarse pattern, cf.
         .prerun-deploy-btn). Unscaled px so it holds at the smallest text-size.
         The chips are ~58px already; this guards against regression + lifts the
         compact info-panel upgrade/sell buttons to a tappable size on touch.

         BL-291 — .tower-btn takes the max() form, not the bare literal.
         The bare 44px sits LATER in this same stylesheet at equal specificity,
         so under pointer:coarse it won outright over the min-height clamp
         (56px … BAY_MIN_H) above — i.e. on the touch devices this rule exists
         to protect, the declared floor was 12 px LOWER than on desktop. That is
         the cap-not-floor inversion modalShell.ts already measured and fixed for
         the modal shell (18.33 vs 31.87 real px on an SE). Nothing moves today —
         the bay renders 76 design px from its own content, above both numbers —
         but the trap is gone: shorten the bay content and the box now falls back
         to the clamp, not to 44. max(44px, X) is >= 44px for every X, so
         BL-109's floor still holds and tests/ui/touchTargets.test.ts matches it
         (the AC-11 shape it already accepts).

         BL-293 — .tt-btn NO LONGER keeps the bare literal. The compressed
         action row gave it a real base height (76 dp, the same as a deploy bay),
         which instantly turned the 44px coarse rule back into a CAP — it would
         have shrunk Upgrade and Sell by 32 dp on exactly the touch devices they
         exist for. The BL-291 guard caught it the moment the base height landed,
         which is what that guard is for. */
      @media (pointer:coarse) {
        .tower-btn { min-height: max(44px, clamp(56px, calc(var(--right-panel-width) * 0.24), ${BAY_MIN_H}px)); }
        .tt-btn { min-height: max(44px, ${TT_BTN_MIN_H}px); }
      }
    `;
    document.head.appendChild(style);
  }

  private readonly tooltip = new InfoTooltip();
  private infoPanelEl: HTMLDivElement | null = null;
  private infoPanelTowerId: string | null = null;
  private upgradeBtnEl: HTMLButtonElement | null = null;
  /** BL-205 — cancels the pending mouse-leave close of the live panel (so a
   *  manual/explicit close doesn't leave a timer that fires into the next panel). */
  private infoPanelLeaveTimerClear: (() => void) | null = null;
  /** BL-297 — the armed SELL confirm's auto-disarm timer, so closing the panel
   *  can never leave a primed destructive control behind a live timer. */
  private sellArmTimer: ReturnType<typeof setTimeout> | null = null;

  private showTooltip(
    anchor: HTMLElement,
    type: Element,
    config: TowerConfig,
  ): void {
    // CR-1: the shared card-style tooltip. IMPACT = DMG·DPS (or the support
    // tag); body = Range·Rate + any special; meta = cost. Flips LEFT of the
    // far-right deploy panel.
    const body: string[] = [];
    let impact: string;
    if (config.damage === 0) {
      impact = t("tower.support");
    } else {
      const dps = (config.damage * config.fireRate).toFixed(1);
      // BL-213 — lead with the glanceable one-word role so a new player reads
      // FUNCTION first (the bay name is flavour), without crowding the bay.
      impact = `${towerTag(type)} · ${t("tower.stat.dmg")}: ${config.damage} · ${t("tower.stat.dps")}: ${dps}`;
      body.push(
        `${t("tower.stat.range")}: ${config.range} · ${t("tower.stat.rate")}: ${config.fireRate.toFixed(1)}/s`,
      );
    }
    if (config.special) {
      body.push(
        describeTowerSpecial(
          config.special as unknown as Record<string, unknown> & {
            type: string;
          },
        ),
      );
    }
    this.tooltip.show(
      anchor,
      {
        name: towerLabel(type),
        accent: config.color,
        tag: towerRole(type),
        impact,
        body,
        meta: t("tower.cost", { cost: config.cost }),
      },
      "left",
    );
  }

  /** P1-4 — touch tap-to-show: surface a bay tooltip on a coarse-pointer tap and
   *  auto-dismiss it (touch has no mouseleave). No-op on desktop (hover already
   *  shows it) and in headless tests (no coarse pointer). */
  private tooltipAutoHide: number | null = null;
  private touchPreviewTooltip(show: () => void): void {
    if (!isTouchTier()) return;
    show();
    if (this.tooltipAutoHide !== null) clearTimeout(this.tooltipAutoHide);
    this.tooltipAutoHide = window.setTimeout(() => {
      this.tooltipAutoHide = null;
      this.hideTooltip();
    }, 3500);
  }

  private hideTooltip(): void {
    if (this.tooltipAutoHide !== null) {
      clearTimeout(this.tooltipAutoHide);
      this.tooltipAutoHide = null;
    }
    this.tooltip.hide();
  }

  private showFirewallTooltip(anchor: HTMLElement): void {
    this.tooltip.show(
      anchor,
      {
        name: t("tower.firewall"),
        accent: COLORS.SENTINEL_GREEN,
        tag: t("tower.firewall.role"),
        impact: `${t("tower.firewall.tag")} · ${t("tower.firewall.support")}`,
        body: [t("tower.firewall.special"), t("tower.firewall.tip")],
        meta: t("tower.cost", { cost: FIREWALL_DEFAULTS.cost }),
      },
      "left",
    );
  }

  /** Show persistent info panel for a placed tower (selected via click) */
  showTowerInfo(
    tower: Tower,
    /** GA-18 (Spec C+): now carries the DESCRIPTION too. The 12 synergies each
     *  ship a real description with a full EN+DE translation, and no code path
     *  read any of them — the player saw a bare name like "Steam" that says
     *  nothing about what it does. */
    activeSynergies?: { name: string; desc: string }[],
    actions?: {
      onUpgrade?: () => void;
      onSell?: () => void;
      canAffordUpgrade?: boolean;
      sellPrice?: number;
      /** BL-205 — invoked when the panel dismisses itself on mouse-leave (after a
       *  genuine mouseenter). The host clears the tower selection so the per-frame
       *  render loop does not immediately re-open the panel. */
      onDismiss?: () => void;
    },
  ): void {
    const upgradeCost = tower.getDisplayUpgradeCost();

    // Skip recreate if same tower is already shown
    const towerId = `${tower.col}_${tower.row}_${tower.level}`;
    if (this.infoPanelEl && this.infoPanelTowerId === towerId) {
      if (actions) {
        this.refreshUpgradeAffordability(
          actions.canAffordUpgrade ?? false,
          upgradeCost,
        );
        const sellBtn =
          this.infoPanelEl.querySelector<HTMLButtonElement>(".tt-btn-sell");
        // BL-299 (owner, build 98) — this fast path runs on EVERY re-show of the
        // already-open panel (credits tick while a tower is selected), and a bare
        // textContent write collapsed the button's two spans into one small line:
        // the owner saw a full-size UPGRADE beside a shrunken "VERKAUF ¢52".
        // Third instance of this exact defect (BL-293 affordability, BL-297 arm),
        // hence the guard test that now covers every writer.
        if (sellBtn && actions.sellPrice != null) {
          if (!sellBtn.classList.contains("tt-btn-sell-armed")) {
            setStackedButtonLabel(
              sellBtn,
              `${t("tower.sell.label")} · ${t("tower.sell.price", { price: actions.sellPrice })}`,
            );
          }
          sellBtn.setAttribute(
            "aria-label",
            t("tower.sell", { price: actions.sellPrice }),
          );
        }
      }
      return;
    }

    this.hideTowerInfo();
    const sellPrice =
      actions?.sellPrice ?? Math.floor(tower.totalInvested * SELL_MULTIPLIER);
    const isGold = tower.isGold();
    // Upgrade preview — the tower's REAL next-level stats (profile-aware), so the
    // panel never promises a gain the runtime won't deliver (D-262 + towerTruth).
    const preview = tower.previewUpgrade();

    // ── stat rows (BL-204): a dim role label · the value · a delta arrow on the
    //    upgrade preview (DMG 12 → 14 ▲). The DATA is unchanged — profile-aware
    //    previewUpgrade() + the honest effectiveDps (D-262 / BL-177); only the
    //    presentation is richer. The honest effective output LEADS (the naive
    //    DMG×rate misleads for kinetic/virus §B3, so it is no longer shown bare).
    // ── BL-293 — the stat block is a FIXED GRID, and change is carried
    //    structurally rather than by colour alone.
    //
    //    The owner's rule was "unveränderte Stats dürfen visuell niemals wie ein
    //    Upgrade wirken". Colour alone does not hold that during a wave — it
    //    reads as decoration. So: the cell ORDER never varies by tower (the eye
    //    learns one position), a CHANGED stat carries a delta chip at full
    //    brightness, and an UNCHANGED one is dimmed, set smaller and carries a
    //    quiet dash. An arrow means gain in this panel and nothing else.
    //
    //    A support tower (`damage === 0`) SHRINKS the grid to the two stats it
    //    actually has, rather than showing DMG and EFF as zeroes — a zero is a
    //    claim, and the wrong one.
    const cell = (
      label: string,
      value: string,
      unit: string,
      next: string | null,
    ): string => {
      const changed = next != null && next !== value;
      return `<div class="tt-cell${changed ? "" : " tt-hold"}">
          <span class="tt-cl">${label}</span>
          <span class="tt-cv">${value}</span>${unit ? `<span class="tt-cu">${unit}</span>` : ""}
          ${changed ? `<span class="tt-chip">→ ${next}</span>` : `<span class="tt-dash">—</span>`}
        </div>`;
    };
    const cells: string[] = [];
    if (tower.damage === 0) {
      cells.push(
        cell(t("tower.stat.range.short"), `${tower.range}`, "", preview ? `${preview.range}` : null),
        cell(t("tower.stat.rate"), tower.fireRate.toFixed(2), "", preview ? preview.fireRate.toFixed(2) : null),
      );
    } else {
      const eff = effectiveDps(tower);
      // The honest EFF (D-262 / BL-177) also previews: the same profile-aware
      // next-level stats the runtime will deliver, run through the same formula.
      const effNext = preview
        ? effectiveDps({
            ...tower,
            damage: preview.damage,
            fireRate: preview.fireRate,
            level: tower.level + 1,
          } as unknown as Tower)
        : null;
      cells.push(
        cell(t("tower.stat.dmg"), `${tower.damage}`, "", preview ? `${preview.damage}` : null),
        cell(t("tower.stat.eff"), eff.toFixed(0), "/s", effNext != null ? effNext.toFixed(0) : null),
        cell(t("tower.stat.rate"), tower.fireRate.toFixed(2), "", preview ? preview.fireRate.toFixed(2) : null),
        cell(t("tower.stat.range.short"), `${tower.range}`, "", preview ? `${preview.range}` : null),
      );
    }
    const statsHtml =
      (tower.damage === 0 ? `<div class="tt-support-line">${t("tower.support")}</div>` : "") +
      `<div class="tt-stats">${cells.join("")}</div>`;

    // ── BL-293 — the mechanic as ROWS, not a paragraph. At GOLD the signature
    //    lives in the feature card, so the star rows are not doubled there.
    const mechRows = tower.special
      ? towerMechanicRows(
          tower.special as unknown as Record<string, unknown> & { type: string },
          tower.level,
          tower.element,
          true, // BL-204 — the feature card owns the forward tease
        )
      : [];
    const specialHtml = mechRows
      .filter((r) => !(isGold && r.star))
      .map(
        (r) =>
          `<div class="tt-mech${r.star ? " tt-mech-star" : " tt-mech-quiet"}">
            <span class="tt-mk">${r.k}</span>${r.v ? `<span class="tt-mv">${r.v}</span>` : ""}
          </div>`,
      )
      .join("");

    // ── BL-293 / OQ-1 — one compact line per synergy: name, then the concrete
    //    effect. The owner's bar is "in deutlich unter einer Sekunde erfassbar",
    //    and their constraint was no extra interaction layer — so there is no
    //    tap, no expand and no tooltip here. Measured budget at 300 dp panel
    //    width: ~42 characters, which is why the effect text is a clause and not
    //    a sentence. The NAMES stay English by owner decision (OQ-4).
    const synergyHtml = (activeSynergies ?? [])
      .map(
        (sy) =>
          `<div class="tt-syn"><b>${sy.name}</b>${sy.desc ? `<span class="tt-syn-dot">·</span><span class="tt-syn-v">${sy.desc}</span>` : ""}</div>`,
      )
      .join("");

    // ── the feature card (BL-204) — ALWAYS populated so the panel reads as ONE
    //    stable size (owner). Three HONEST states: the UPCOMING ★ capability the
    //    tower is progressing toward (lookahead, BL-204 fix — not just level+1, so
    //    the crown no longer shows at the first upgrade), else (already GOLD) the
    //    active signature, else (every pre-GOLD beat unlocked) the GOLD pinnacle
    //    teaser. The ★/👑 are the transparent OpenAI emblems; alt="" + the CSS card
    //    mean a failed image degrades to text.
    //    BL-296 — the card renders the beat STRUCTURED (kicker · level · NAME on
    //    the identity row, the effect clause quiet beneath it), which is the
    //    frozen mockup's `.nx` block. It is deliberately the same two rows in
    //    every state, so the panel's height no longer moves with the length of
    //    the beat's copy — and it never calls a locked beat "active".
    const upcomingBeat = upcomingCapabilityBeat(tower);
    const featCard = (
      beat: CapBeat | null,
      kicker: string,
      emblem: string,
      extraClass = "",
    ): string => {
      const lv =
        beat && !beat.gold
          ? `<span class="tt-feat-lv">${t("tower.cap.lv", { level: beat.level })}</span>`
          : "";
      const name = beat?.name ?? "";
      const value = beat?.value ?? t("tower.gold.tease");
      return `<div class="tt-feat${extraClass}">
          <img class="tt-feat-emblem${extraClass ? " tt-feat-emblem--gold" : ""}" src="${emblem}" alt="" aria-hidden="true" />
          <div class="tt-feat-body">
            <div class="tt-feat-head"><span class="tt-feat-kicker">${kicker}</span>${lv}<span class="tt-feat-name">${name}</span></div>
            <div class="tt-feat-text">${value}</div>
          </div>
        </div>`;
    };
    const featureHtml = upcomingBeat
      ? featCard(upcomingBeat, t("tower.next"), "/img/panel/cap-star.webp")
      : featCard(
          goldSignatureBeat(tower.element),
          t("tower.gold.badge"),
          "/img/panel/gold-crown.webp",
          isGold ? " tt-feat--gold tt-feat--armed" : " tt-feat--gold",
        );

    // Action buttons (CR1: HUD-panel upgrade + sell) — premium CTA material
    let actionsHtml = "";
    if (actions) {
      const upgradeUi = upgradeButtonState({
        upgradeCost,
        canAfford: actions.canAffordUpgrade ?? false,
      });
      // ── BL-293 — Upgrade is primary by FILL, LIGHT and WIDTH; Sell is
      //    secondary by TREATMENT, never by size. The owner was explicit that
      //    Sell must stay "ein vollwertiges, großes Touch-Target und nicht ein
      //    untergeordneter Textbutton", so both carry the same height and each
      //    gets its own price line. Measured before this change: 33.1 × 75.56
      //    real px with a 3.06 real px gap between them — a destructive action
      //    one thumb-width from the primary one.
      const upLabel = upgradeUi.label.split(" · ");
      actionsHtml = `<div class="tt-actions">
        <button class="tt-btn tt-btn-upgrade ${upgradeUi.stateClass}" ${upgradeUi.disabled ? "disabled" : ""}>
          <span class="tt-btn-t">${upLabel[0]}</span>${upLabel[1] ? `<span class="tt-btn-s">${upLabel[1]}</span>` : ""}
        </button>
        <button class="tt-btn tt-btn-sell">
          <span class="tt-btn-t">${t("tower.sell.label")}</span><span class="tt-btn-s">${t("tower.sell.price", { price: sellPrice })}</span>
        </button>
      </div>`;
    }

    // ── tier ladder (BL-204): L1→L(GOLD−1) pips + the GOLD crown apex, replacing
    //    the bare "Lv.N" text. Pips light ≤ the current level (the current one
    //    enlarged); at GOLD every pip + the crown light + the --gold skin applies.
    const pipCount = Math.max(1, tower.maxLevel - 1);
    let pips = "";
    for (let i = 1; i <= pipCount; i++) {
      const on = isGold || i <= tower.level;
      const cur = !isGold && i === tower.level;
      pips += `<span class="tt-pip${on ? " tt-pip-on" : ""}${cur ? " tt-pip-cur" : ""}"></span>`;
    }
    const ladderHtml = `<div class="tt-ladder" role="img" aria-label="${isGold ? t("tower.gold.badge") : `Lv.${tower.level}/${pipCount}`}">${pips}<img class="tt-crown" src="/img/panel/gold-crown.webp" alt="" aria-hidden="true" /></div>`;

    const panel = document.createElement("div");
    panel.className = "tower-tooltip tower-info-panel";
    panel.style.setProperty("--tt-color", tower.color);
    // BL-175 / D-266 — at GOLD the panel takes the apex skin (the lit crown + the
    // gold name/feature); the tier is shown by the ladder, not a "Lv.N" string.
    if (isGold) panel.classList.add("tower-info-panel--gold");
    panel.innerHTML = `
      <div class="tt-head">
        <div class="tt-name">${TOWER_LABELS[tower.element] ? towerLabel(tower.element) : tower.element}</div>
        <div class="tt-role">${TOWER_ROLES[tower.element] ? towerRole(tower.element) : ""}</div>
      </div>
      <div class="tt-ladderrow">${ladderHtml}</div>
      ${statsHtml}
      ${specialHtml}
      ${synergyHtml}
      ${featureHtml}
      ${actionsHtml}
    `;

    // Wire button click handlers
    if (actions) {
      const upgradeBtn =
        panel.querySelector<HTMLButtonElement>(".tt-btn-upgrade");
      const sellBtn = panel.querySelector(".tt-btn-sell");
      this.upgradeBtnEl = upgradeBtn;
      if (upgradeBtn && actions.onUpgrade) {
        upgradeBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          actions.onUpgrade!();
        });
      }
      if (sellBtn && actions.onSell) {
        const sb = sellBtn as HTMLButtonElement;
        const onSell = actions.onSell;
        let armed = false;
        const disarm = (): void => {
          armed = false;
          this.clearSellArmTimer();
          sb.classList.remove("tt-btn-sell-armed");
          // BL-297 — restore the two SPANS, not a flat textContent: the price is
          // the second line and must come back with the label.
          setStackedButtonLabel(
            sb,
            `${t("tower.sell.label")} · ${t("tower.sell.price", { price: sellPrice })}`,
          );
          sb.setAttribute("aria-label", t("tower.sell", { price: sellPrice }));
        };
        sb.addEventListener("click", (e) => {
          e.stopPropagation();
          // BL-119 / BL-297 — two presses on the SAME control at the SAME
          // position: the first ARMS (VERKAUF → BESTÄTIGEN in place, same box,
          // same price), the second sells. A stray first tap costs nothing — it
          // disarms on a tap anywhere outside the button, when the panel closes,
          // and on its own after SELL_ARM_TIMEOUT_MS.
          if (armed) {
            onSell();
          } else {
            armed = true;
            this.armSellInPlace(sb, sellPrice);
            this.clearSellArmTimer();
            this.sellArmTimer = setTimeout(disarm, SELL_ARM_TIMEOUT_MS);
          }
        });
        // A tap anywhere else in the panel (or on the board) cancels the confirm.
        panel.addEventListener("click", () => {
          if (armed) disarm();
        });
      }

      this.refreshUpgradeAffordability(
        actions.canAffordUpgrade ?? false,
        upgradeCost,
      );
    }

    // BL-205 (reconciling BL-126) — close on mouse-leave, but ONLY after a
    // genuine mouseenter. BL-126 removed leave-dismiss because a tower placed
    // where the cursor already is lands the panel under the pointer and a stray
    // move fires `mouseleave` with NO prior `mouseenter` → the panel flashed
    // closed before the operator could interact. The `hasEntered` latch fixes
    // that exactly: opening (even under the cursor) never dismisses; the panel
    // closes only once the cursor has truly entered and then left. A short grace
    // timer absorbs the tower→panel gap / sub-pixel jitter (a re-enter cancels
    // it), so the panel never closes while the cursor is travelling to a button.
    // The click / ESC / re-click / phase-change paths are untouched, and
    // coarse/touch pointers (no hover) keep relying on those. `onDismiss` clears
    // the host's tower selection so the per-frame render loop, which re-derives
    // the panel from `selectedTower`, does not immediately reopen it.
    let hasEntered = false;
    let leaveTimer: ReturnType<typeof setTimeout> | null = null;
    const clearLeaveTimer = (): void => {
      if (leaveTimer !== null) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }
    };
    panel.addEventListener("mouseenter", () => {
      hasEntered = true;
      clearLeaveTimer();
    });
    panel.addEventListener("mouseleave", () => {
      if (!hasEntered) return; // never flash-close on open (BL-126)
      clearLeaveTimer();
      leaveTimer = setTimeout(() => {
        leaveTimer = null;
        // Only act if THIS panel is still the live one (a re-select may have
        // replaced it). hideTowerInfo + the host's selection clear keep it shut.
        if (this.infoPanelEl !== panel) return;
        actions?.onDismiss?.();
        this.hideTowerInfo();
      }, TOWER_INFO_LEAVE_GRACE_MS);
    });

    // Mount inside the fixed-stage container so the panel scales with the game;
    // positions below are converted into stage-local design space. Falls back to
    // <body> in headless/test DOM (where getStageFit() is an identity fit).
    (document.getElementById("game-container") ?? document.body).appendChild(
      panel,
    );
    this.infoPanelEl = panel;
    this.infoPanelTowerId = towerId;
    this.infoPanelLeaveTimerClear = clearLeaveTimer;

    // Position near the tower on canvas. The panel lives inside the transformed
    // stage, so style.left/top are stage-local — but getBoundingClientRect() is
    // viewport space. Convert the tower's on-screen point into design space and
    // clamp to the design bounds. offsetHeight is layout px (already design
    // space). With no stage (headless) the fit is identity → original math.
    const canvas = document.getElementById(
      "game-canvas",
    ) as HTMLCanvasElement | null;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const fit = getStageFit();
      const scaleX = rect.width / (GRID_WIDTH * CELL_SIZE);
      const tower0 = clientPointToStage(
        rect.left + tower.x * scaleX,
        rect.top + tower.y * scaleX,
      );
      const cellStage = (CELL_SIZE * scaleX) / fit.scale;
      const boundsW = fit.designW || window.innerWidth;
      const boundsH = fit.designH || window.innerHeight;
      // BL-293 — two bugs lived here and only a rendered edge-tower found them:
      //
      //   1. `panelWidth` was the literal 220, stale since the panel became
      //      `withUiScale(236)`. The "would it overflow?" test therefore used a
      //      width the panel has not had for a long time, so the flip-to-left
      //      fired too late and a right-edge tower pushed the panel OFF SCREEN
      //      (measured +46 px past the viewport on 852×393).
      //   2. It clamped to the whole STAGE, but the stage's outer columns are
      //      the two HUD rails. A right-edge tower laid its panel across the
      //      deploy bays (measured 185 dp of overlap) — over the very controls
      //      the panel exists to complement.
      //
      // Both are fixed by measuring the real box and clamping to the BOARD.
      // BL-299 (owner, build 98) — `offsetWidth` is ALREADY in stage/design px:
      // the panel lives inside the CSS-transformed stage, and offsetWidth reports
      // the UNtransformed layout width. Dividing it by `fit.scale` inflated a
      // 399 dp panel to 913.7 dp, which dragged `maxX` from 1230 down to 715 —
      // so the "would it overflow?" test fired for every tower past the board's
      // first third, the flip-to-left then overshot, and the clamp parked the
      // panel on a CONSTANT x (611 dp) regardless of where the tower was.
      // Measured: towers at cells 18/1, 18/6 and 18/10 all put the panel at the
      // same place, 231 real px away from their tower. BL-293 introduced this
      // while fixing a stale literal — the literal was in the right unit.
      const panelWidth = panel.offsetWidth || 236;
      const minX = (fit.leftRail ?? 0) + 4;
      const maxX = boundsW - (fit.rightRail ?? 0) - panelWidth - 4;

      // Prefer right of tower; fall back to its left when that would cross the
      // right rail — then clamp, so neither side can ever win by overflowing.
      let leftPos = tower0.x + cellStage + 8;
      if (leftPos > maxX) {
        leftPos = tower0.x - panelWidth - 8;
      }
      leftPos = Math.max(minX, Math.min(leftPos, Math.max(minX, maxX)));
      // Vertically: above tower center, clamped to the stage
      let topPos = tower0.y - 60;
      if (topPos < 0) topPos = 4;
      const panelHeight = panel.offsetHeight;
      if (topPos + panelHeight > boundsH) {
        topPos = Math.max(4, boundsH - panelHeight - 8);
      }

      panel.style.left = `${leftPos}px`;
      panel.style.top = `${topPos}px`;
    } else {
      panel.style.right = "220px";
      panel.style.top = "80px";
    }
  }

  refreshUpgradeAffordability(
    canAfford: boolean,
    upgradeCost: number | null,
  ): void {
    if (!this.upgradeBtnEl) return;

    const state = upgradeButtonState({ upgradeCost, canAfford });
    this.upgradeBtnEl.disabled = state.disabled;
    this.upgradeBtnEl.classList.toggle(
      "tt-btn-maxed",
      state.stateClass === "tt-btn-maxed",
    );
    this.upgradeBtnEl.classList.toggle(
      "tt-btn-unaffordable",
      state.stateClass === "tt-btn-unaffordable",
    );
    // BL-293 — the button is now two stacked spans (label over cost), so a bare
    // textContent write would flatten it back to one line on the first live
    // affordability refresh. Rewrite the SPANS, not the node.
    setStackedButtonLabel(this.upgradeBtnEl, state.label);
  }

  /** BL-119 / BL-297 — arm the SELL button as the confirm IN PLACE. The same
   *  element keeps its slot and its shape, so the second press lands exactly
   *  where the first did and the panel does not move a pixel between them.
   *
   *  BL-297 (owner HUMAN-Befund on build 97) — this used to write
   *  `sellBtn.textContent`, which destroyed the button's two spans (the price
   *  vanished and an 18-character "VERKAUF BESTÄTIGEN" was crammed into a 66.6
   *  real-px box), and then grew a 138.8 dp block below the row carrying
   *  "TURM ABBAUEN · ERSTATTUNG ¢62 (83%) · ⚠ PFAD WIRD NEU BERECHNET" plus its
   *  own CANCEL — a second dialog inside the dialog, which pushed the panel from
   *  151.7 to 214.9 real px on a 393 px viewport. The owner's rule: the confirm
   *  IS the button. The refund is already on it; the percentage, the decommission
   *  title and the path warning are not part of the decision. */
  private armSellInPlace(sellBtn: HTMLButtonElement, sellPrice: number): void {
    sellBtn.classList.add("tt-btn-sell-armed");
    setStackedButtonLabel(
      sellBtn,
      `${t("tower.sell.confirm")} · ${t("tower.sell.price", { price: sellPrice })}`,
    );
    sellBtn.setAttribute(
      "aria-label",
      `${t("sell.confirm")} · ${t("sell.refund")} ¢${sellPrice}`,
    );
  }

  /** BL-297 — drop the armed-confirm timer. Every close path calls this, so a
   *  timer can never fire into a panel that has already been replaced. */
  private clearSellArmTimer(): void {
    if (this.sellArmTimer !== null) {
      clearTimeout(this.sellArmTimer);
      this.sellArmTimer = null;
    }
  }

  /** Hide the tower info panel */
  hideTowerInfo(): void {
    // BL-297 — closing the panel cancels the confirm; re-opening never inherits
    // a primed destructive control.
    this.clearSellArmTimer();
    // BL-205 — cancel any pending mouse-leave close so its timer can't fire into
    // a panel mounted later (every close path routes through here).
    if (this.infoPanelLeaveTimerClear) {
      this.infoPanelLeaveTimerClear();
      this.infoPanelLeaveTimerClear = null;
    }
    if (this.infoPanelEl) {
      this.infoPanelEl.remove();
      this.infoPanelEl = null;
      this.infoPanelTowerId = null;
      this.upgradeBtnEl = null;
    }
  }
}
